import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wordmark } from "@/components/shieldx/Logo";
import {
  ArrowRight, ArrowDown, ShieldCheck, Bot, Network, Siren, OctagonAlert, Workflow, Building2,
  Landmark, ShieldAlert, User, Lock, RotateCcw, FileClock, Check, ChevronDown, Server, Globe,
  Database, Cloud, Layers, Activity, KeyRound, ScanSearch, Fingerprint, Scale, Radar, Boxes,
  Cpu, EyeOff, Droplet, Image as ImageIcon, FlaskConical, Crosshair, Gauge, ShieldHalf,
} from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05, ease: "easeOut" } }),
};

function Reveal({ children, i = 0, className = "" }) {
  return (
    <motion.div className={className} variants={reveal} custom={i} initial="hidden"
      whileInView="show" viewport={{ once: true, margin: "-60px" }}>
      {children}
    </motion.div>
  );
}

function Section({ id, eyebrow, title, intro, children, className = "" }) {
  return (
    <section id={id} className={`mx-auto max-w-7xl px-6 py-20 lg:py-24 ${className}`}>
      {eyebrow && <Reveal><p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-blue-400/80">{eyebrow}</p></Reveal>}
      {title && <Reveal i={1}><h2 className="max-w-3xl font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">{title}</h2></Reveal>}
      {intro && <Reveal i={2}><p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-400">{intro}</p></Reveal>}
      {children}
    </section>
  );
}

// Vertical architecture flow (nodes + connectors)
function VFlow({ steps, accentIndex = [] }) {
  return (
    <div className="flex flex-col items-stretch">
      {steps.map((s, i) => (
        <div key={s.label} className="flex flex-col items-center">
          <motion.div variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            className={`group w-full rounded-xl border px-5 py-3.5 text-center transition-colors ${accentIndex.includes(i) ? "border-blue-500/40 bg-blue-600/10" : "border-slate-800 bg-slate-900/60 hover:border-slate-700"}`}>
            <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${accentIndex.includes(i) ? "text-blue-300" : "text-slate-300"}`}>{s.label}</p>
            {s.note && <p className="mt-1 text-xs text-slate-500">{s.note}</p>}
          </motion.div>
          {i < steps.length - 1 && <ArrowDown className="my-1.5 h-4 w-4 text-slate-600" />}
        </div>
      ))}
    </div>
  );
}

// Horizontal pipeline (wraps on small screens)
function HFlow({ steps }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <motion.span variants={reveal} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="rounded-lg border border-slate-800 bg-slate-900/70 px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-slate-300">{s}</motion.span>
          {i < steps.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-600" />}
        </div>
      ))}
    </div>
  );
}

const CAPABILITIES = [
  { icon: KeyRound, title: "Authorization", body: "Verify whether an identity is actually permitted to perform a specific action — before it happens." },
  { icon: Bot, title: "Autonomous Activity Protection", body: "Govern AI agents, bots and automated systems interacting with protected assets. Capability never grants authority." },
  { icon: Fingerprint, title: "Identity Verification", body: "Establish who — or what — is making the request before anything else is evaluated." },
  { icon: Scale, title: "Policy Enforcement", body: "Apply deterministic rules before any consequential action is allowed. Default is deny." },
  { icon: Radar, title: "Threat Detection", body: "Surface suspicious behaviour and security signals, scored from LOW to CRITICAL." },
  { icon: Siren, title: "Incident Response", body: "Structured command: Detect → Block → Contain → Recover, with evidence at every step." },
  { icon: FileClock, title: "Auditability", body: "Maintain tamper-evident, traceable evidence for every security decision." },
  { icon: RotateCcw, title: "Recovery", body: "Controlled, validated response and recovery — a compromised state is never blindly restored." },
];

const PIPELINE = ["Request", "Identity", "Authentication", "Authorization", "Scope", "Policy", "Context", "Risk", "Rate Limit", "Decision", "Audit"];
const OUTCOMES = [
  { t: "ALLOW", c: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5" },
  { t: "CHALLENGE", c: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5" },
  { t: "DENY", c: "text-amber-300 border-amber-500/30 bg-amber-500/5" },
  { t: "BLOCK", c: "text-orange-300 border-orange-500/30 bg-orange-500/5" },
  { t: "ISOLATE", c: "text-rose-300 border-rose-500/30 bg-rose-500/5" },
];

const PROTECTED = [
  [Globe, "Websites"], [Layers, "Web Applications"], [Network, "APIs"], [Server, "Servers"],
  [Database, "Databases"], [Cloud, "Cloud Infrastructure"], [Workflow, "Digital Services"],
  [Landmark, "Government Services"], [ShieldHalf, "Critical Systems"], [Cpu, "AI / Autonomous Integrations"],
];

const AI_DEFENSE = [
  { icon: ScanSearch, t: "Defensive Prompt Injection", d: "Detect and neutralize instruction-override attempts in untrusted content — without ever executing them." },
  { icon: Crosshair, t: "AI Decoy / Honey-Prompt", d: "Isolated synthetic decoys reveal suspicious automated agents, never exposing real systems." },
  { icon: Droplet, t: "Content Provenance", d: "Provenance and anti-scraping controls protect digital content and detect unauthorized collection." },
  { icon: EyeOff, t: "Privacy & Data Masking", d: "Mask, tokenize or redact sensitive fields before data crosses a trust boundary." },
  { icon: Gauge, t: "Model Extraction Defense", d: "Query, burst and behavioural controls defend AI-powered APIs from systematic extraction." },
  { icon: ImageIcon, t: "Synthetic Media Detection", d: "Evidence-based classification of manipulated or synthetic images, audio and video." },
];

const PRINCIPLES = [
  "Capability ≠ Authority", "Autonomy ≠ Authority", "Data ≠ Authority", "Identity must be verified",
  "Authorization must be explicit", "Policy remains deterministic", "Audit must remain traceable", "Fail closed where appropriate",
];

const MODULES = {
  "Core Protection": ["Authorization", "Identity", "Policy", "Asset Protection", "Threat Detection", "Incident Response"],
  "AI Security": ["Prompt Injection Defense", "AI Decoy", "Content Provenance", "Privacy & Data Masking", "Model Extraction Defense", "Synthetic Media Detection"],
  "Security Operations": ["Defense Wall", "Attack Simulation", "Incident 360°", "Asset 360°", "Policy 360°", "Authorization 360°", "Audit", "Reports", "Emergency Control"],
};

const PLANES = [
  { t: "Control Plane", d: "Policies, authorization, configuration and governance.", icon: Scale },
  { t: "Enforcement Plane", d: "Real-time decision and protection enforcement.", icon: ShieldCheck },
  { t: "Intelligence Plane", d: "Detection, correlation, analysis and recommendations.", icon: Radar },
  { t: "Protected Digital Systems", d: "Websites, APIs, servers, databases, cloud and critical systems.", icon: Boxes },
];

const TRUST = ["Zero-Trust principles", "Least privilege", "Deterministic authorization", "Tenant isolation",
  "Evidence-based security", "Tamper-evident audit", "Role-based access", "Emergency controls", "Secure integrations"];

const FAQ = [
  { q: "Is ShieldX an antivirus or a WAF?", a: "No. ShieldX is an authorization and protection layer for the autonomous era. It establishes a controlled boundary around registered digital assets and evaluates who or what is attempting access, what action is requested, and whether that action is authorized. Where traditional tools focus on detecting malicious traffic, ShieldX focuses on a deeper question: does this autonomous system have the authority to perform this action?" },
  { q: "Does ShieldX guarantee systems can never be hacked?", a: "No responsible technology can promise that. ShieldX enforces something more important: controlled authority. For assets properly registered, verified and connected to its protection boundary, unauthorized activity is continuously evaluated against identity, authorization, policy, risk and protection controls, and can be Detected → Denied → Blocked → Isolated → Recovered. The objective is to make unauthorized access difficult, visible and controllable." },
  { q: "How does ShieldX treat AI agents?", a: "As a digital identity — not an automatically trusted authority. An agent may be highly capable and autonomous; none of that grants permission. Before a protected action is permitted, ShieldX evaluates Identity → Authentication → Authorization → Action → Scope → Policy → Risk. Missing authorization or a policy violation can be Denied, Challenged, Blocked or escalated for human approval." },
];

export default function Landing() {
  const [open, setOpen] = useState(0);
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Wordmark markSize={28} />
          <nav className="hidden items-center gap-8 text-sm text-slate-400 lg:flex">
            {["What it Does", "How it Works", "AI Defense", "Architecture", "FAQ"].map((n) => (
              <a key={n} href={`#${n.toLowerCase().replace(/\s+/g, "-")}`} className="sx-link-underline hover:text-slate-100">{n}</a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-signin" className="text-sm font-medium text-slate-300 hover:text-white">Sign in</Link>
            <Link to="/register" data-testid="nav-get-started" className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">Explore ShieldX</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="relative overflow-hidden border-b border-slate-800 sx-grid">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_30%_0%,rgba(37,99,235,0.10),transparent)]" />
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 py-24 lg:grid-cols-[1.1fr_0.9fr] lg:py-28">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-3 py-1 font-mono text-[11px] uppercase tracking-widest text-slate-400">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> Digital Authorization & Autonomous Activity Protection
            </span>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              The Digital Shield for the <span className="text-blue-500">Autonomous Era</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-400">
              ShieldX verifies identity, evaluates authorization, enforces policy, and protects digital systems
              from unauthorized autonomous activity.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-explore" className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-500">
                Explore ShieldX <ArrowRight className="h-4 w-4" />
              </Link>
              <a href="#how-it-works" data-testid="hero-architecture" className="rounded-lg border border-slate-700 bg-slate-900/60 px-5 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800">View Security Architecture</a>
            </div>
            <p className="mt-8 font-mono text-xs uppercase tracking-widest text-slate-600">Capability does not equal authority.</p>
          </div>
          {/* Hero control-plane visualization (architecture, not decoration) */}
          <div className="relative rounded-2xl border border-slate-800 bg-slate-900/40 p-6 shadow-2xl">
            <p className="mb-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">ShieldX Control Plane</p>
            <VFlow accentIndex={[2]} steps={[
              { label: "Autonomous System", note: "AI agent · bot · service" },
              { label: "Identity" },
              { label: "ShieldX Protocol", note: "verify · authorize · enforce" },
              { label: "Authorization" },
              { label: "Policy" },
              { label: "Protected Digital System" },
              { label: "Audit", note: "tamper-evident evidence" },
            ]} />
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="border-b border-slate-800 bg-slate-900/30">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-6 py-6 text-slate-500">
          {[[Server, "Servers"], [Globe, "Websites & APIs"], [Database, "Databases"], [Cloud, "Cloud"], [Cpu, "AI Systems"]].map(([I, t]) => (
            <span key={t} className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest"><I className="h-4 w-4" /> {t}</span>
          ))}
        </div>
      </div>

      {/* What ShieldX Does */}
      <Section id="what-it-does" eyebrow="What ShieldX Does" title="Authority-first protection for a connected, autonomous world."
        intro="Eight core capabilities working as one control plane — every consequential action is verified, authorized, enforced and recorded.">
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} i={i % 4}>
              <div className="h-full rounded-xl border border-slate-800 bg-slate-900/50 p-6 transition-colors hover:border-blue-500/30">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-blue-400"><c.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display text-base font-semibold text-slate-100">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* How ShieldX Protects — decision pipeline */}
      <Section id="how-it-works" eyebrow="How ShieldX Protects" title="The security decision pipeline."
        intro="Every request flows through a deterministic sequence before a decision is reached. Trust is never implicit."
        className="border-y border-slate-800 bg-slate-900/20">
        <Reveal><div className="relative mt-8 overflow-hidden rounded-2xl border border-slate-800">
          <img src="https://images.unsplash.com/photo-1569660424259-87e64a80f6fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Enterprise data center infrastructure" loading="lazy" className="h-56 w-full object-cover opacity-80 sm:h-72" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
          <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">Digital infrastructure · security · control · protection</p>
        </div></Reveal>
        <div className="mt-8"><HFlow steps={PIPELINE} /></div>
        <div className="mt-8">
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Possible Decisions</p>
          <div className="flex flex-wrap gap-2.5">
            {OUTCOMES.map((o) => (
              <span key={o.t} className={`rounded-lg border px-4 py-2 font-mono text-xs font-semibold tracking-widest ${o.c}`}>{o.t}</span>
            ))}
          </div>
        </div>
        <p className="mt-6 font-mono text-xs uppercase tracking-widest text-slate-500">Deterministic · fail-closed · fully audited</p>
      </Section>

      {/* Protected assets ecosystem */}
      <Section eyebrow="What ShieldX Protects" title="One protection boundary across your digital estate.">
        <div className="relative mt-12">
          <div className="mx-auto mb-10 w-fit rounded-2xl border border-blue-500/40 bg-blue-600/10 px-8 py-4 text-center">
            <p className="font-display text-lg font-bold text-slate-50">ShieldX</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-blue-300">Protection Boundary</p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {PROTECTED.map(([I, t], i) => (
              <Reveal key={t} i={i % 5}>
                <div className="flex flex-col items-center gap-2.5 rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-6 text-center transition-colors hover:border-blue-500/30">
                  <I className="h-6 w-6 text-blue-400" />
                  <span className="font-mono text-[11px] uppercase tracking-wider text-slate-300">{t}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Section>

      {/* AI Security Defense */}
      <Section id="ai-defense" eyebrow="AI Security Defense" title="Protect AI workflows — without giving AI authority over ShieldX."
        intro="ShieldX analyzes untrusted content and controls autonomous behaviour. It can analyze; it can never be commanded by the data it inspects."
        className="border-y border-slate-800 bg-slate-900/20">
        <Reveal><div className="relative mt-8 overflow-hidden rounded-2xl border border-slate-800">
          <img src="https://images.unsplash.com/photo-1717386255785-59c670564341?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Autonomous systems interacting with protected enterprise infrastructure" loading="lazy" className="h-56 w-full object-cover opacity-80 sm:h-64" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
          <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">Autonomous system → identity → authorization → protected infrastructure</p>
        </div></Reveal>
        <div className="mt-8"><HFlow steps={["Untrusted Input", "Detect", "Classify", "Authorize", "Policy", "Control", "Audit"]} /></div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AI_DEFENSE.map((m, i) => (
            <Reveal key={m.t} i={i % 3}>
              <div className="h-full rounded-xl border border-slate-800 bg-slate-900/50 p-6 transition-colors hover:border-blue-500/30">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-blue-400"><m.icon className="h-5 w-5" /></span>
                <h3 className="mt-4 font-display text-base font-semibold text-slate-100">{m.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Autonomous system protection — capability vs authority */}
      <Section eyebrow="Autonomous System Protection" title="Capability does not equal authority.">
        <div className="mt-10 grid items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
            <VFlow accentIndex={[2, 3]} steps={[
              { label: "AI Agent", note: "requests an action" },
              { label: "ShieldX verifies identity" },
              { label: "ShieldX checks authorization" },
              { label: "Policy evaluation" },
              { label: "Decision" },
              { label: "Protected System" },
            ]} />
          </div>
          <div>
            <div className="rounded-xl border border-blue-500/30 bg-blue-600/10 p-6">
              <p className="font-display text-xl font-bold text-slate-50">AI can request access.</p>
              <p className="mt-1 font-display text-xl font-bold text-blue-300">AI cannot assume access.</p>
            </div>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              As autonomous systems take more actions on our behalf, the real risk is not intelligence — it is
              unchecked authority. ShieldX keeps increased capability from ever becoming increased permission.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {["Scoped", "Time-limited", "Revocable", "Human approval for high-impact"].map((t) => (
                <div key={t} className="flex items-center gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-blue-400" /> {t}</div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Verification → Protection lifecycle */}
      <Section eyebrow="Trust Lifecycle" title="Only verified, connected assets reach the protected state."
        className="border-y border-slate-800 bg-slate-900/20">
        <div className="mt-8"><HFlow steps={["Registered", "Verification Required", "Verified", "Connection", "Configuration", "Protection", "Protected"]} /></div>
        <p className="mt-6 max-w-2xl text-sm text-slate-500">An asset is never shown as PROTECTED until ownership is verified and its protection boundary is active.</p>
      </Section>

      {/* Defense Wall */}
      <Section eyebrow="Defense Wall" title="Observe, classify, decide, respond.">
        <Reveal><div className="relative mt-8 overflow-hidden rounded-2xl border border-slate-800">
          <img src="https://images.unsplash.com/photo-1648611648035-805e9ae87437?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="Enterprise security operations center monitoring protected systems" loading="lazy" className="h-56 w-full object-cover opacity-80 sm:h-72" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10" />
          <p className="absolute bottom-4 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300">Security operations · monitoring · incidents · authorization</p>
        </div></Reveal>
        <div className="mt-8"><HFlow steps={["Request", "Observe", "Classify", "Identify", "Authorize", "Policy", "Risk", "Decision", "Response", "Audit"]} /></div>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {OUTCOMES.map((o) => <span key={o.t} className={`rounded-lg border px-4 py-2 font-mono text-xs font-semibold tracking-widest ${o.c}`}>{o.t}</span>)}
        </div>
        <p className="mt-6 font-mono text-xs uppercase tracking-widest text-slate-500">Restrained enterprise defense — no fake counters, no fake live threats</p>
      </Section>

      {/* Platform architecture — planes */}
      <Section id="architecture" eyebrow="Platform Architecture" title="Three planes, one protection boundary."
        className="border-y border-slate-800 bg-slate-900/20">
        <div className="mt-10 flex flex-col gap-4">
          {PLANES.map((p, i) => (
            <div key={p.t} className="flex flex-col items-center">
              <Reveal i={i} className="w-full">
                <div className={`flex items-center gap-4 rounded-xl border px-6 py-5 ${i === 3 ? "border-blue-500/40 bg-blue-600/10" : "border-slate-800 bg-slate-900/50"}`}>
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-slate-700 bg-slate-800/60 text-blue-400"><p.icon className="h-5 w-5" /></span>
                  <div><h3 className="font-display text-base font-semibold text-slate-100">{p.t}</h3><p className="mt-0.5 text-sm text-slate-400">{p.d}</p></div>
                </div>
              </Reveal>
              {i < PLANES.length - 1 && <ArrowDown className="my-1.5 h-4 w-4 text-slate-600" />}
            </div>
          ))}
        </div>
      </Section>

      {/* Security principles */}
      <Section eyebrow="Security Principles" title="The rules that never bend.">
        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p} i={i % 4}>
              <div className="flex h-full items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/50 px-5 py-4">
                <Lock className="h-4 w-4 shrink-0 text-blue-400" />
                <span className="font-display text-sm font-semibold text-slate-100">{p}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Product module map */}
      <Section eyebrow="Product Modules" title="Everything inside the ShieldX platform." className="border-y border-slate-800 bg-slate-900/20">
        <div className="mt-10 grid gap-5 lg:grid-cols-3">
          {Object.entries(MODULES).map(([group, items], i) => (
            <Reveal key={group} i={i}>
              <div className="h-full rounded-xl border border-slate-800 bg-slate-900/50 p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-blue-300">{group}</p>
                <div className="mt-4 space-y-2">
                  {items.map((it) => (
                    <div key={it} className="flex items-center gap-2 text-sm text-slate-300"><span className="h-1.5 w-1.5 rounded-full bg-slate-600" /> {it}</div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Attack simulation + trust */}
      <Section eyebrow="Attack Simulation & Enterprise Trust" title="Validate your defenses — safely.">
        <div className="mt-8"><HFlow steps={["Authorize", "Scope", "Safe Simulation", "Detection", "Response", "Evidence", "Defense Validation"]} /></div>
        <p className="mt-5 max-w-3xl text-sm text-slate-400">Simulations are explicitly authorized, scoped and non-destructive — they validate detection and response and produce evidence-based scorecards. No destructive exploitation is ever performed.</p>
        <div className="mt-8 flex flex-wrap gap-2.5">
          {TRUST.map((t) => <span key={t} className="rounded-full border border-slate-700 bg-slate-900/70 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-slate-300">{t}</span>)}
        </div>
        <p className="mt-6 text-xs text-slate-600">We never claim: 100% secure · impossible to hack · never down · 100% phishing-proof · 100% AI-proof.</p>
      </Section>

      {/* FAQ */}
      <Section id="faq" eyebrow="FAQ" title="Clear answers. No hype." className="border-t border-slate-800 bg-slate-900/20">
        <div className="mt-8 max-w-3xl divide-y divide-slate-800 rounded-xl border border-slate-800 bg-slate-900/50">
          {FAQ.map((item, i) => (
            <div key={i}>
              <button onClick={() => setOpen(open === i ? -1 : i)} data-testid={`faq-${i}`}
                className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left">
                <span className="font-display text-base font-semibold text-slate-100">{item.q}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-slate-500 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="px-6 pb-5 text-sm leading-relaxed text-slate-400">{item.a}</p>}
            </div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section id="contact">
        <div className="rounded-2xl border border-slate-800 bg-gradient-to-br from-blue-600/10 to-slate-900/40 p-10 text-center lg:p-16">
          <h2 className="font-display text-3xl font-bold text-slate-50 sm:text-4xl">The Digital Shield for the Autonomous Era</h2>
          <div className="mx-auto mt-6 max-w-md space-y-1 font-mono text-sm uppercase tracking-wider text-slate-400">
            <p>Verify the identity.</p><p>Authorize the action.</p><p>Enforce the policy.</p>
            <p>Protect the system.</p><p className="text-blue-300">Preserve the evidence.</p>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="cta-register" className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-semibold text-white hover:bg-blue-500">Explore ShieldX <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/login" data-testid="cta-signin" className="rounded-lg border border-slate-700 bg-slate-900/60 px-6 py-3 text-sm font-semibold text-slate-200 hover:bg-slate-800">Sign in</Link>
          </div>
          <p className="mt-6 text-sm text-slate-500">Questions? Contact <a href="mailto:info@vs-solutions.org" data-testid="cta-support-email" className="font-medium text-blue-400 hover:text-blue-300">info@vs-solutions.org</a></p>
        </div>
      </Section>

      <footer className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-6 py-14">
          <div className="grid gap-10 md:grid-cols-[1.4fr_1fr]">
            <div className="max-w-md">
              <Wordmark markSize={24} />
              <p className="mt-3 font-display text-sm font-semibold text-slate-300">The Digital Shield for the Autonomous Era</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">
                Enterprise security, digital authorization and autonomous activity protection.<br />
                Powered by Vijay Software Solutions Pvt Ltd.
              </p>
              <p className="mt-3 text-sm text-slate-400">
                Support: <a href="mailto:info@vs-solutions.org" data-testid="footer-support-email" className="font-medium text-blue-400 hover:text-blue-300">info@vs-solutions.org</a>
              </p>
            </div>
            <div className="flex flex-col gap-2.5 text-sm md:items-end">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">Navigate</p>
              <a href="#what-it-does" className="text-slate-400 hover:text-slate-200">Product</a>
              <a href="#architecture" className="text-slate-400 hover:text-slate-200">Security</a>
              <a href="#how-it-works" className="text-slate-400 hover:text-slate-200">Architecture</a>
              <Link to="/about" className="text-slate-400 hover:text-slate-200">Documentation</Link>
              <a href="mailto:info@vs-solutions.org" className="text-slate-400 hover:text-slate-200">Contact / Support</a>
            </div>
          </div>
          <div className="mt-10 flex flex-col items-center justify-between gap-2 border-t border-slate-800/70 pt-6 sm:flex-row">
            <p className="text-xs text-slate-600">© Vijay Software Solutions Pvt Ltd. All rights reserved.</p>
            <p className="font-mono text-[11px] tracking-wide text-slate-500">Defense-in-depth · We never promise the impossible.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
